/**
 * DriftChip — compact inline indicator shown next to items that have drift.
 *
 * PR 6b Phase 2. Renders nothing when severity is undefined/null so callers
 * can always render <DriftChip severity={…} onClick={…} /> without checking.
 *
 * Severity → colour/label mapping:
 *   minor         → warning (amber)   label "Drift"
 *   major         → error (red)       label "Drift"
 *   orphan        → error outlined    label "Drift"   (item gone from catalog)
 *   indeterminate → default           label "?"       (catalog service unavailable)
 */
import React from "react";
import { Chip, Tooltip } from "@mui/material";
import type { DriftSeverity } from "../../model/Proposal";

interface Props {
  severity: DriftSeverity | undefined;
  onClick: () => void;
}

const INDETERMINATE_TOOLTIP =
  "Drift indeterminado — serviço de catálogo indisponível";

const DriftChip: React.FC<Props> = ({ severity, onClick }) => {
  if (!severity) return null;

  if (severity === "indeterminate") {
    return (
      <Tooltip title={INDETERMINATE_TOOLTIP}>
        {/* Tooltip requires a focusable child — wrap span so Chip keyboard
            events still fire correctly. */}
        <Chip
          label="?"
          size="small"
          color="default"
          onClick={onClick}
          clickable
          role="button"
          aria-label={INDETERMINATE_TOOLTIP}
          data-testid="drift-chip-indeterminate"
          sx={{ ml: 0.5, cursor: "pointer" }}
        />
      </Tooltip>
    );
  }

  const colorMap: Record<
    Exclude<DriftSeverity, "indeterminate">,
    "warning" | "error"
  > = {
    minor: "warning",
    major: "error",
    orphan: "error",
  };

  const ariaLabelMap: Record<
    Exclude<DriftSeverity, "indeterminate">,
    string
  > = {
    minor: "Drift menor detectado",
    major: "Drift maior detectado",
    orphan: "Item não encontrado no catálogo",
  };

  return (
    <Chip
      label="Drift"
      size="small"
      color={colorMap[severity]}
      variant={severity === "orphan" ? "outlined" : "filled"}
      onClick={onClick}
      clickable
      role="button"
      aria-label={ariaLabelMap[severity]}
      data-testid={`drift-chip-${severity}`}
      sx={{ ml: 0.5, cursor: "pointer" }}
    />
  );
};

export default DriftChip;
